<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MFixedAssetEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    fixedAsset: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const [data] = args;

    if (!props.target) {
      return data;
    }

    const fieldsToSubmit = targetConfig.value[props.target].fieldsToSubmit;
    if (fieldsToSubmit.indexOf('*') > -1) {
      return data;
    }

    return _.pick(data, fieldsToSubmit);
  };

  const formId = ref(null);
  const defaultTitle = ref(
    $fn.value.pgettext('FixedAsset', 'Edit General Information')
  );
  const targetConfig = ref({
    general: {
      title: $fn.value.pgettext('FixedAsset', 'Edit general data'),
      fieldsToSubmit: ['*'],
    },
    wasteBin: {
      title: $fn.value.pgettext('FixedAsset', 'Edit waste bin data'),
      fieldsToSubmit: ['id', 'etag', 'waste_bin'],
    },
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      defaultTitle,
      targetConfig,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.fixedAsset,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fixedAsset');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_0gd8deb5p_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_y0kmvrles_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_xjhuoco4b_getActionArgs = async ($event) => {
    return {
      objectType: 'fixed_asset',
      op: 'upsert',
      data: getPayload(props.fixedAsset),
    };
  };

  const act_7315092382398562_closeModal_0gd8deb5p =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_0gd8deb5p_getActionArgs,
    });
  const inst_evh_7315092382398562_close_lmt7ba09p =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_0gd8deb5p,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_lmt7ba09p],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_bozz9fu8j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_6tvpcdr3b =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_bozz9fu8j,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_6tvpcdr3b],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_niahibzz0 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_q7moatsta =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_niahibzz0,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_q7moatsta],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_y0kmvrles =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_y0kmvrles_getActionArgs,
    });
  const inst_evh_1040167445267876_success_fjfxccnph =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_y0kmvrles,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_crud_xjhuoco4b = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_xjhuoco4b_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_fjfxccnph],
  });
  const inst_evh_1040167445267876_submit_g914fygh1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_xjhuoco4b,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_g914fygh1],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_poh7r0b9g =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_c00489au1 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_poh7r0b9g,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_c00489au1],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_lmt7ba09p,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_6tvpcdr3b,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_q7moatsta,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_fjfxccnph,
    inst_evh_1040167445267876_submit_g914fygh1,
    save: inst_evh_1040167445267876_submit_g914fygh1,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_c00489au1,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_lmt7ba09p = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_lmt7ba09p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_6tvpcdr3b = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_6tvpcdr3b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_q7moatsta = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_q7moatsta.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_fjfxccnph = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_fjfxccnph.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_g914fygh1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_g914fygh1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_c00489au1 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_c00489au1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MFixedAssetEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="fixedAsset"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <MFixedAssetEditorFieldset
            :fixedAsset="fixedAsset"
            :formId="formId"
            :target="target"
          >
          </MFixedAssetEditorFieldset>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.fixedAsset || !forms.fixedAsset.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
